import { canSwap, isMovableCell, isStaticObstacle, swapCells } from "./board";
import { cloneBoard } from "./grid";
import { hasAnyMatch } from "./match";
import { activationFromSwap, isBomb, isWipe } from "./specials";
import { Rng } from "./rng";
import type { BoardState, CollapseAnimStep, LevelConfig, Position, TileData } from "./types";

export const DEAD_BOARD_RESHUFFLE_STEP_COUNT = 5;
export const DEAD_BOARD_RESHUFFLE_TOTAL_SEC = 3;
export const DEAD_BOARD_RESHUFFLE_STEP_SEC = DEAD_BOARD_RESHUFFLE_TOTAL_SEC / DEAD_BOARD_RESHUFFLE_STEP_COUNT;

export const DEAD_BOARD_RESHUFFLE_OVERLAY_TEXT = {
  headline: "NO MORE MOVES",
  subline: "RESHUFFLE"
} as const;

export type DeadBoardReshuffleStrategy = "shuffle" | "forced_pattern" | "compact_cluster";

export interface PlannedDeadBoardReshuffle {
  board: BoardState;
  steps: CollapseAnimStep[];
  strategy: DeadBoardReshuffleStrategy;
  playable: boolean;
}

interface HintCellOptions {
  includeSpecialSwaps?: boolean;
}

interface SwapOutcome {
  specialTriggered: boolean;
  matched: boolean;
}

interface PointLike {
  row: number;
  col: number;
}

interface ResolvedDeadBoardReshuffle {
  strategy: DeadBoardReshuffleStrategy;
  playable: boolean;
}

interface CompactCandidateRank {
  playable: boolean;
  clean: boolean;
  adjacencyCount: number;
  distanceTwoPairs: number;
  centroidDistance: number;
}

function cloneTile(tile: TileData): TileData {
  return {
    color: tile.color,
    special: tile.special,
    lockHp: tile.lockHp
  };
}

function comparePositionsRowMajor(a: Position, b: Position): number {
  if (a.row !== b.row) {
    return a.row - b.row;
  }
  return a.col - b.col;
}

function positionKey(pos: Position): string {
  return `${pos.row},${pos.col}`;
}

function manhattanDistance(a: PointLike, b: PointLike): number {
  return Math.abs(a.row - b.row) + Math.abs(a.col - b.col);
}

function centerOfPositions(positions: Position[]): PointLike {
  if (positions.length === 0) {
    return { row: 0, col: 0 };
  }
  const totals = positions.reduce((acc, pos) => ({
    row: acc.row + pos.row,
    col: acc.col + pos.col
  }), { row: 0, col: 0 });
  return {
    row: totals.row / positions.length,
    col: totals.col / positions.length
  };
}

function compareByDistanceToPoint(a: Position, b: Position, point: PointLike): number {
  const distanceDiff = manhattanDistance(a, point) - manhattanDistance(b, point);
  if (distanceDiff !== 0) {
    return distanceDiff;
  }
  return comparePositionsRowMajor(a, b);
}

function forEachPotentialSwap(
  board: BoardState,
  callback: (from: Position, to: Position) => void
): void {
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      const from = { row, col };
      const candidates: Position[] = [
        { row, col: col + 1 },
        { row: row + 1, col }
      ];
      for (const to of candidates) {
        if (to.row >= board.size || to.col >= board.size) continue;
        if (!canSwap(board, from, to)) continue;
        callback(from, to);
      }
    }
  }
}

function evaluateSwapOutcome(board: BoardState, from: Position, to: Position): SwapOutcome {
  const activation = activationFromSwap(board, from, to);
  swapCells(board, from, to);
  const matched = hasAnyMatch(board);
  swapCells(board, from, to);
  return {
    specialTriggered: activation.triggered,
    matched
  };
}

function hasDirectTapSpecialMove(board: BoardState): boolean {
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      const cell = board.cells[row][col];
      if (
        cell.kind === "TILE"
        && cell.tile != null
        && (isBomb(cell.tile.special) || isWipe(cell.tile.special))
      ) {
        return true;
      }
    }
  }
  return false;
}

export function hasValidMove(board: BoardState): boolean {
  if (hasDirectTapSpecialMove(board)) {
    return true;
  }
  let hasMove = false;
  forEachPotentialSwap(board, (from, to) => {
    if (hasMove) return;
    const outcome = evaluateSwapOutcome(board, from, to);
    if (outcome.specialTriggered || outcome.matched) {
      hasMove = true;
    }
  });
  return hasMove;
}

export function findHintCells(board: BoardState, options?: HintCellOptions): Position[] {
  const includeSpecialSwaps = options?.includeSpecialSwaps ?? true;
  const cells = new Map<string, Position>();
  forEachPotentialSwap(board, (from, to) => {
    const outcome = evaluateSwapOutcome(board, from, to);
    if (!outcome.matched && (!includeSpecialSwaps || !outcome.specialTriggered)) {
      return;
    }
    cells.set(positionKey(from), from);
    cells.set(positionKey(to), to);
  });
  return [...cells.values()];
}

function allMovablePositions(board: BoardState): Position[] {
  const positions: Position[] = [];
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      if (isMovableCell(board.cells[row][col])) {
        positions.push({ row, col });
      }
    }
  }
  return positions;
}

function allEligiblePositions(board: BoardState): Position[] {
  const positions: Position[] = [];
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      if (!isStaticObstacle(board.cells[row][col])) {
        positions.push({ row, col });
      }
    }
  }
  return positions;
}

function tileKey(tile: TileData): string {
  return `${tile.color}|${tile.special}|${tile.lockHp}`;
}

function tileAt(board: BoardState, pos: Position): TileData | null {
  const cell = board.cells[pos.row]?.[pos.col];
  if (cell?.kind !== "TILE" || cell.tile == null) {
    return null;
  }
  return cell.tile;
}

function cloneTilePool(board: BoardState, positions: Position[]): TileData[] {
  return positions.map((pos) => {
    const tile = tileAt(board, pos);
    if (tile == null) {
      throw new Error("Expected movable tile while cloning reshuffle pool");
    }
    return cloneTile(tile);
  });
}

function applyTilePool(board: BoardState, positions: Position[], pool: TileData[]): void {
  for (let index = 0; index < positions.length; index += 1) {
    const pos = positions[index];
    const tile = pool[index];
    if (pos == null || tile == null) {
      continue;
    }
    board.cells[pos.row][pos.col] = { kind: "TILE", tile: cloneTile(tile) };
  }
}

function clearEligibleCells(board: BoardState): void {
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      if (!isStaticObstacle(board.cells[row][col])) {
        board.cells[row][col] = { kind: "EMPTY", tile: null };
      }
    }
  }
}

function copyBoardState(target: BoardState, source: BoardState): void {
  target.gravity = source.gravity;
  target.crateGravity = source.crateGravity;
  target.cells = source.cells.map((row) => row.map((cell) => (
    cell.tile == null
      ? { kind: cell.kind, tile: null }
      : {
          kind: cell.kind,
          tile: cloneTile(cell.tile)
        }
  )));
}

function countDifferingTilePositions(board: BoardState, other: BoardState, positions: Position[]): number {
  let total = 0;
  for (const pos of positions) {
    const left = tileAt(board, pos);
    const right = tileAt(other, pos);
    const same = left == null && right == null
      ? true
      : left != null && right != null && tileKey(left) === tileKey(right);
    if (!same) {
      total += 1;
    }
  }
  return total;
}

function pickBestSourceIndex(
  availableSources: Position[],
  target: Position,
  predicate: (source: Position) => boolean
): number {
  let bestIndex = -1;
  let bestDistance = Number.NEGATIVE_INFINITY;
  for (let index = 0; index < availableSources.length; index += 1) {
    const source = availableSources[index];
    if (source == null || !predicate(source)) {
      continue;
    }
    const distance = manhattanDistance(source, target);
    const boostedDistance = distance === 0 ? -1 : distance;
    if (boostedDistance > bestDistance) {
      bestDistance = boostedDistance;
      bestIndex = index;
    }
  }
  return bestIndex;
}

function buildReshuffleStep(
  fromBoard: BoardState,
  toBoard: BoardState,
  durationOverrideSec: number
): CollapseAnimStep {
  const fixedTargets = new Set<string>();
  const availableSources = allMovablePositions(fromBoard).filter((pos) => {
    const fromTile = tileAt(fromBoard, pos);
    const toTile = tileAt(toBoard, pos);
    const fixed = fromTile != null && toTile != null && tileKey(fromTile) === tileKey(toTile);
    if (fixed) {
      fixedTargets.add(positionKey(pos));
    }
    return !fixed;
  });
  const targetPositions = allMovablePositions(toBoard).filter((pos) => !fixedTargets.has(positionKey(pos)));
  const motions: CollapseAnimStep["motions"] = [];
  const unmatchedTargets: Position[] = [];

  for (const target of targetPositions) {
    const targetTile = tileAt(toBoard, target);
    if (targetTile == null) {
      continue;
    }
    const exactSourceIndex = pickBestSourceIndex(
      availableSources,
      target,
      (source) => {
        const sourceTile = tileAt(fromBoard, source);
        return sourceTile != null && tileKey(sourceTile) === tileKey(targetTile);
      }
    );
    if (exactSourceIndex < 0) {
      unmatchedTargets.push(target);
      continue;
    }
    const source = availableSources.splice(exactSourceIndex, 1)[0];
    const sourceTile = source == null ? null : tileAt(fromBoard, source);
    if (source == null || sourceTile == null) {
      continue;
    }
    motions.push({
      fromRow: source.row,
      fromCol: source.col,
      toRow: target.row,
      toCol: target.col,
      spawn: false,
      tile: cloneTile(sourceTile)
    });
  }

  for (const target of unmatchedTargets) {
    const carrierSourceIndex = pickBestSourceIndex(availableSources, target, () => true);
    if (carrierSourceIndex < 0) {
      continue;
    }
    const source = availableSources.splice(carrierSourceIndex, 1)[0];
    const sourceTile = source == null ? null : tileAt(fromBoard, source);
    if (source == null || sourceTile == null) {
      continue;
    }
    motions.push({
      fromRow: source.row,
      fromCol: source.col,
      toRow: target.row,
      toCol: target.col,
      spawn: false,
      tile: cloneTile(sourceTile)
    });
  }

  return {
    tier: 3,
    motions,
    clearedCells: [],
    crateBreakCells: [],
    matchedGroups: [],
    chainIndex: 0,
    boardSnapshot: cloneBoard(fromBoard),
    specialFx: "RESHUFFLE",
    overlayText: {
      headline: DEAD_BOARD_RESHUFFLE_OVERLAY_TEXT.headline,
      subline: DEAD_BOARD_RESHUFFLE_OVERLAY_TEXT.subline
    },
    durationOverrideSec
  };
}

function collectEligibleRegions(board: BoardState): Position[][] {
  const visited = new Set<string>();
  const regions: Position[][] = [];

  for (const start of allEligiblePositions(board)) {
    const startKey = positionKey(start);
    if (visited.has(startKey)) {
      continue;
    }

    const region: Position[] = [];
    const queue: Position[] = [start];
    visited.add(startKey);

    while (queue.length > 0) {
      const current = queue.shift();
      if (current == null) {
        continue;
      }
      region.push(current);

      const neighbors: Position[] = [
        { row: current.row - 1, col: current.col },
        { row: current.row + 1, col: current.col },
        { row: current.row, col: current.col - 1 },
        { row: current.row, col: current.col + 1 }
      ];
      for (const next of neighbors) {
        if (
          next.row < 0
          || next.col < 0
          || next.row >= board.size
          || next.col >= board.size
          || isStaticObstacle(board.cells[next.row][next.col])
        ) {
          continue;
        }
        const nextKey = positionKey(next);
        if (visited.has(nextKey)) {
          continue;
        }
        visited.add(nextKey);
        queue.push(next);
      }
    }

    region.sort(comparePositionsRowMajor);
    regions.push(region);
  }

  return regions;
}

function pickClusterFootprintRegion(board: BoardState): Position[] {
  const regions = collectEligibleRegions(board);
  if (regions.length === 0) {
    return [];
  }

  const boardCenter = { row: (board.size - 1) / 2, col: (board.size - 1) / 2 };
  regions.sort((left, right) => {
    if (left.length !== right.length) {
      return right.length - left.length;
    }
    const leftCenter = centerOfPositions(left);
    const rightCenter = centerOfPositions(right);
    const centerDistanceDiff = manhattanDistance(leftCenter, boardCenter) - manhattanDistance(rightCenter, boardCenter);
    if (centerDistanceDiff !== 0) {
      return centerDistanceDiff;
    }
    return comparePositionsRowMajor(left[0] ?? { row: 0, col: 0 }, right[0] ?? { row: 0, col: 0 });
  });

  return regions[0] ?? [];
}

function compactClusterDestinations(board: BoardState, tileCount: number): { positions: Position[]; center: PointLike } {
  const footprint = pickClusterFootprintRegion(board);
  const center = centerOfPositions(footprint);
  const footprintKeys = new Set(footprint.map(positionKey));
  const orderedFootprint = [...footprint].sort((a, b) => compareByDistanceToPoint(a, b, center));
  if (orderedFootprint.length >= tileCount) {
    return {
      positions: orderedFootprint.slice(0, tileCount),
      center
    };
  }

  const spillover = allEligiblePositions(board)
    .filter((pos) => !footprintKeys.has(positionKey(pos)))
    .sort((a, b) => compareByDistanceToPoint(a, b, center));

  return {
    positions: [...orderedFootprint, ...spillover].slice(0, tileCount),
    center
  };
}

function snakeOrder(positions: Position[]): Position[] {
  const rows = [...new Set(positions.map((pos) => pos.row))].sort((a, b) => a - b);
  const byRow = new Map<number, Position[]>();
  for (const pos of positions) {
    const row = byRow.get(pos.row) ?? [];
    row.push(pos);
    byRow.set(pos.row, row);
  }

  const ordered: Position[] = [];
  rows.forEach((rowValue, index) => {
    const rowPositions = [...(byRow.get(rowValue) ?? [])].sort(comparePositionsRowMajor);
    if (index % 2 === 1) {
      rowPositions.reverse();
    }
    ordered.push(...rowPositions);
  });
  return ordered;
}

function buildCellOrders(destinations: Position[]): Position[][] {
  const rowMajor = [...destinations].sort(comparePositionsRowMajor);
  const snake = snakeOrder(destinations);
  return [
    rowMajor,
    snake,
    [...rowMajor].reverse(),
    [...snake].reverse()
  ];
}

function buildTileOrders(tiles: TileData[]): TileData[][] {
  const groupedByColor = new Map<number, TileData[]>();
  for (const tile of tiles) {
    const group = groupedByColor.get(tile.color) ?? [];
    group.push(cloneTile(tile));
    groupedByColor.set(tile.color, group);
  }

  const groups = [...groupedByColor.entries()]
    .map(([color, groupTiles]) => ({ color, tiles: groupTiles }))
    .sort((left, right) => {
      if (left.tiles.length !== right.tiles.length) {
        return right.tiles.length - left.tiles.length;
      }
      return left.color - right.color;
    });

  const grouped = groups.flatMap((group) => group.tiles.map(cloneTile));
  const groupedReverse = [...groups].reverse().flatMap((group) => group.tiles.map(cloneTile));
  const roundRobinGroups = groups.map((group) => ({
    color: group.color,
    tiles: group.tiles.map(cloneTile)
  }));
  const roundRobin: TileData[] = [];
  while (roundRobinGroups.some((group) => group.tiles.length > 0)) {
    for (const group of roundRobinGroups) {
      const tile = group.tiles.shift();
      if (tile != null) {
        roundRobin.push(cloneTile(tile));
      }
    }
  }

  return [grouped, roundRobin, groupedReverse];
}

function buildCompactClusterBoard(
  templateBoard: BoardState,
  cellOrder: Position[],
  tiles: TileData[]
): BoardState {
  const candidate = cloneBoard(templateBoard);
  clearEligibleCells(candidate);
  for (let index = 0; index < cellOrder.length; index += 1) {
    const pos = cellOrder[index];
    const tile = tiles[index];
    if (pos == null || tile == null) {
      continue;
    }
    candidate.cells[pos.row][pos.col] = {
      kind: "TILE",
      tile: cloneTile(tile)
    };
  }
  return candidate;
}

function countSameColorAdjacencies(board: BoardState): number {
  let total = 0;
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      const current = board.cells[row][col];
      if (current.kind !== "TILE" || current.tile == null) {
        continue;
      }
      const right = board.cells[row]?.[col + 1];
      const down = board.cells[row + 1]?.[col];
      if (right?.kind === "TILE" && right.tile != null && right.tile.color === current.tile.color) {
        total += 1;
      }
      if (down?.kind === "TILE" && down.tile != null && down.tile.color === current.tile.color) {
        total += 1;
      }
    }
  }
  return total;
}

function countSameColorDistanceTwoPairs(board: BoardState): number {
  const positions = allMovablePositions(board);
  let total = 0;
  for (let leftIndex = 0; leftIndex < positions.length; leftIndex += 1) {
    const leftPos = positions[leftIndex];
    const leftTile = leftPos == null ? null : tileAt(board, leftPos);
    if (leftPos == null || leftTile == null) {
      continue;
    }
    for (let rightIndex = leftIndex + 1; rightIndex < positions.length; rightIndex += 1) {
      const rightPos = positions[rightIndex];
      const rightTile = rightPos == null ? null : tileAt(board, rightPos);
      if (rightPos == null || rightTile == null || rightTile.color !== leftTile.color) {
        continue;
      }
      if (manhattanDistance(leftPos, rightPos) === 2) {
        total += 1;
      }
    }
  }
  return total;
}

function totalCentroidDistance(board: BoardState, center: PointLike): number {
  return allMovablePositions(board).reduce((total, pos) => total + manhattanDistance(pos, center), 0);
}

function rankCompactCandidate(board: BoardState, center: PointLike): CompactCandidateRank {
  const clean = !hasAnyMatch(board);
  return {
    playable: clean && hasValidMove(board),
    clean,
    adjacencyCount: countSameColorAdjacencies(board),
    distanceTwoPairs: countSameColorDistanceTwoPairs(board),
    centroidDistance: totalCentroidDistance(board, center)
  };
}

function compareCompactCandidateRank(left: CompactCandidateRank, right: CompactCandidateRank): number {
  if (left.playable !== right.playable) {
    return left.playable ? 1 : -1;
  }
  if (left.clean !== right.clean) {
    return left.clean ? 1 : -1;
  }
  if (left.adjacencyCount !== right.adjacencyCount) {
    return left.adjacencyCount - right.adjacencyCount;
  }
  if (left.distanceTwoPairs !== right.distanceTwoPairs) {
    return left.distanceTwoPairs - right.distanceTwoPairs;
  }
  if (left.centroidDistance !== right.centroidDistance) {
    return right.centroidDistance - left.centroidDistance;
  }
  return 0;
}

function improveCompactClusterBoard(
  board: BoardState,
  positions: Position[],
  center: PointLike
): BoardState {
  let bestBoard = cloneBoard(board);
  let bestRank = rankCompactCandidate(bestBoard, center);

  for (let iteration = 0; iteration < 32; iteration += 1) {
    let improvedBoard: BoardState | null = null;
    let improvedRank: CompactCandidateRank | null = null;

    for (let leftIndex = 0; leftIndex < positions.length; leftIndex += 1) {
      const leftPos = positions[leftIndex];
      const leftTile = leftPos == null ? null : tileAt(bestBoard, leftPos);
      if (leftPos == null || leftTile == null) {
        continue;
      }
      for (let rightIndex = leftIndex + 1; rightIndex < positions.length; rightIndex += 1) {
        const rightPos = positions[rightIndex];
        const rightTile = rightPos == null ? null : tileAt(bestBoard, rightPos);
        if (
          rightPos == null
          || rightTile == null
          || tileKey(leftTile) === tileKey(rightTile)
        ) {
          continue;
        }

        const candidate = cloneBoard(bestBoard);
        swapCells(candidate, leftPos, rightPos);
        const candidateRank = rankCompactCandidate(candidate, center);
        if (compareCompactCandidateRank(candidateRank, bestRank) <= 0) {
          continue;
        }
        if (improvedRank == null || compareCompactCandidateRank(candidateRank, improvedRank) > 0) {
          improvedBoard = candidate;
          improvedRank = candidateRank;
        }
      }
    }

    if (improvedBoard == null || improvedRank == null) {
      break;
    }
    bestBoard = improvedBoard;
    bestRank = improvedRank;
  }

  return bestBoard;
}

function compactClusterFallback(board: BoardState): ResolvedDeadBoardReshuffle & { board: BoardState } {
  const tiles = cloneTilePool(board, allMovablePositions(board));
  if (tiles.length === 0) {
    return {
      board: cloneBoard(board),
      strategy: "compact_cluster",
      playable: false
    };
  }

  const { positions: destinations, center } = compactClusterDestinations(board, tiles.length);
  const cellOrders = buildCellOrders(destinations);
  const tileOrders = buildTileOrders(tiles);

  let bestBoard: BoardState | null = null;
  let bestRank: CompactCandidateRank | null = null;

  for (const cellOrder of cellOrders) {
    for (const tileOrder of tileOrders) {
      const candidate = buildCompactClusterBoard(board, cellOrder, tileOrder);
      const improved = improveCompactClusterBoard(candidate, cellOrder, center);
      const rank = rankCompactCandidate(improved, center);
      if (bestRank == null || compareCompactCandidateRank(rank, bestRank) > 0) {
        bestBoard = improved;
        bestRank = rank;
      }
    }
  }

  const resolvedBoard = bestBoard ?? cloneBoard(board);
  const resolvedRank = bestRank ?? rankCompactCandidate(resolvedBoard, center);
  return {
    board: resolvedBoard,
    strategy: "compact_cluster",
    playable: resolvedRank.playable
  };
}

function injectForcedPattern(board: BoardState, level: LevelConfig, rng: Rng): boolean {
  const regions: Position[] = [];
  for (let row = 0; row < board.size - 1; row += 1) {
    for (let col = 0; col < board.size - 2; col += 1) {
      const cells = [
        board.cells[row][col],
        board.cells[row][col + 1],
        board.cells[row][col + 2],
        board.cells[row + 1][col],
        board.cells[row + 1][col + 1],
        board.cells[row + 1][col + 2]
      ];
      if (cells.every(isMovableCell)) {
        regions.push({ row, col });
      }
    }
  }

  rng.shuffleInPlace(regions);
  const colorOrder = Array.from({ length: level.tileTypes }, (_, index) => index);

  for (const region of regions) {
    const { row, col } = region;
    const cells = [
      board.cells[row][col],
      board.cells[row][col + 1],
      board.cells[row][col + 2],
      board.cells[row + 1][col],
      board.cells[row + 1][col + 1],
      board.cells[row + 1][col + 2]
    ];
    if (!cells.every(isMovableCell)) {
      continue;
    }
    const tiles = cells.map((cell) => cell.tile);
    if (tiles.some((tile) => tile == null)) {
      continue;
    }

    const originals = tiles.map((tile) => cloneTile(tile as TileData));
    const aChoices = [...colorOrder];
    rng.shuffleInPlace(aChoices);

    for (const colorA of aChoices) {
      const nonAChoices = colorOrder.filter((color) => color !== colorA);
      rng.shuffleInPlace(nonAChoices);
      const dChoices = [...colorOrder];
      rng.shuffleInPlace(dChoices);

      for (const colorB of nonAChoices) {
        for (const colorC of nonAChoices) {
          for (const colorD of dChoices) {
            tiles[0]!.color = colorA;
            tiles[1]!.color = colorA;
            tiles[2]!.color = colorB;
            tiles[3]!.color = colorC;
            tiles[4]!.color = colorD;
            tiles[5]!.color = colorA;
            tiles[2]!.lockHp = 0;
            tiles[5]!.lockHp = 0;

            if (!hasAnyMatch(board) && hasValidMove(board)) {
              return true;
            }

            for (let index = 0; index < tiles.length; index += 1) {
              const tile = tiles[index];
              const original = originals[index];
              if (tile == null) continue;
              tile.color = original.color;
              tile.special = original.special;
              tile.lockHp = original.lockHp;
            }
          }
        }
      }
    }
  }
  return false;
}

function applyGuaranteedReshuffle(
  board: BoardState,
  level: LevelConfig,
  rng: Rng,
  attempts = 30
): ResolvedDeadBoardReshuffle {
  const positions = allMovablePositions(board);
  if (positions.length === 0) {
    return {
      strategy: "shuffle",
      playable: false
    };
  }

  const pool = cloneTilePool(board, positions);
  const originalPool = pool.map(cloneTile);

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    rng.shuffleInPlace(pool);
    applyTilePool(board, positions, pool);
    if (!hasAnyMatch(board) && hasValidMove(board)) {
      return {
        strategy: "shuffle",
        playable: true
      };
    }
  }

  applyTilePool(board, positions, originalPool);
  if (!hasAnyMatch(board) && hasValidMove(board)) {
    return {
      strategy: "shuffle",
      playable: true
    };
  }

  if (injectForcedPattern(board, level, rng) && !hasAnyMatch(board) && hasValidMove(board)) {
    return {
      strategy: "forced_pattern",
      playable: true
    };
  }

  const compact = compactClusterFallback(board);
  copyBoardState(board, compact.board);
  return {
    strategy: compact.strategy,
    playable: compact.playable
  };
}

function finalTileLockOrder(board: BoardState): Position[] {
  const positions = allMovablePositions(board);
  const center = centerOfPositions(positions);
  return [...positions].sort((a, b) => compareByDistanceToPoint(a, b, center));
}

function lockCountForIntermediateStep(tileCount: number, stepIndex: number, stepCount: number): number {
  if (tileCount <= 0 || stepCount <= 1 || stepIndex >= stepCount) {
    return tileCount;
  }
  const progress = stepIndex / stepCount;
  return Math.min(tileCount, Math.max(0, Math.ceil(tileCount * progress)));
}

function createIntermediateReshuffleBoard(
  previousBoard: BoardState,
  finalBoard: BoardState,
  lockOrder: Position[],
  lockCount: number,
  rng: Rng
): BoardState {
  const eligiblePositions = allEligiblePositions(finalBoard);
  const finalTilePositions = allMovablePositions(finalBoard);
  const lockKeys = new Set(lockOrder.slice(0, lockCount).map(positionKey));
  const unlockedPositions = finalTilePositions.filter((pos) => !lockKeys.has(positionKey(pos)));
  if (unlockedPositions.length === 0) {
    return cloneBoard(finalBoard);
  }

  const minimumMovedTiles = Math.min(
    unlockedPositions.length,
    Math.max(2, Math.ceil(unlockedPositions.length * 0.3))
  );
  let bestCandidate: BoardState | null = null;
  let bestMovedTiles = Number.NEGATIVE_INFINITY;
  const unlockedPool = cloneTilePool(finalBoard, unlockedPositions);

  for (let attempt = 0; attempt < 30; attempt += 1) {
    const candidate = cloneBoard(finalBoard);
    clearEligibleCells(candidate);

    for (const pos of finalTilePositions) {
      if (!lockKeys.has(positionKey(pos))) {
        continue;
      }
      const tile = tileAt(finalBoard, pos);
      if (tile != null) {
        candidate.cells[pos.row][pos.col] = { kind: "TILE", tile: cloneTile(tile) };
      }
    }

    const pool = unlockedPool.map(cloneTile);
    rng.shuffleInPlace(pool);
    applyTilePool(candidate, unlockedPositions, pool);

    const movedTiles = countDifferingTilePositions(previousBoard, candidate, eligiblePositions);
    const differsFromFinal = countDifferingTilePositions(candidate, finalBoard, eligiblePositions) > 0;
    if (!differsFromFinal || movedTiles <= 0) {
      continue;
    }
    if (movedTiles > bestMovedTiles) {
      bestCandidate = candidate;
      bestMovedTiles = movedTiles;
    }
    if (movedTiles >= minimumMovedTiles) {
      return candidate;
    }
  }

  return bestCandidate ?? cloneBoard(previousBoard);
}

export function guaranteedReshuffle(
  board: BoardState,
  level: LevelConfig,
  rng: Rng,
  attempts = 30
): void {
  void applyGuaranteedReshuffle(board, level, rng, attempts);
}

export function planDeadBoardReshuffle(
  board: BoardState,
  level: LevelConfig,
  rng: Rng,
  options?: {
    durationOverrideSec?: number;
    stepCount?: number;
  }
): PlannedDeadBoardReshuffle {
  const stepCount = Math.max(1, Math.floor(options?.stepCount ?? DEAD_BOARD_RESHUFFLE_STEP_COUNT));
  const durationOverrideSec = Math.max(0.01, options?.durationOverrideSec ?? DEAD_BOARD_RESHUFFLE_STEP_SEC);
  const finalBoard = cloneBoard(board);
  const resolved = applyGuaranteedReshuffle(finalBoard, level, rng);

  const boardStates: BoardState[] = [cloneBoard(board)];
  const lockOrder = finalTileLockOrder(finalBoard);
  let previousBoard = cloneBoard(board);

  for (let stepIndex = 1; stepIndex < stepCount; stepIndex += 1) {
    const nextBoard = createIntermediateReshuffleBoard(
      previousBoard,
      finalBoard,
      lockOrder,
      lockCountForIntermediateStep(lockOrder.length, stepIndex, stepCount),
      rng
    );
    boardStates.push(nextBoard);
    previousBoard = cloneBoard(nextBoard);
  }
  boardStates.push(cloneBoard(finalBoard));

  const steps: CollapseAnimStep[] = [];
  for (let index = 0; index < stepCount; index += 1) {
    const fromBoard = boardStates[index];
    const toBoard = boardStates[index + 1];
    if (fromBoard == null || toBoard == null) {
      continue;
    }
    steps.push(buildReshuffleStep(fromBoard, toBoard, durationOverrideSec));
  }

  return {
    board: finalBoard,
    steps,
    strategy: resolved.strategy,
    playable: resolved.playable
  };
}
